'use client';

import { useState } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Address } from 'viem';

import { OverlayLoading } from '@/components/shared/OverlayLoading';
import { SwapAmountInputs } from '@/components/shared/SwapAmountInputs';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { usePersonaKYC } from '@/hooks/usePersonaKYC';
import { useTokenImport } from '@/hooks/useTokenImport';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { removeCommas } from '@/lib/utils/removeCommas';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';
import { T_Pool } from '@/ts/types/T_Pool';

import { calculateTokensRemaining } from './tokenDataUtils';
import { TokenImportCheckbox } from './TokenImportCheckbox';

interface I_TokenPurchaseProps {
  selectedToken: T_Pool;
  onAmountChange?: (amount: number) => void;
  className?: string;
  closeModal?: () => void;
  hideLearnMore?: boolean;
  onPoolDataUpdate?: (boughtTokenAmount: number) => void;
}

export function TokenPurchase({
  selectedToken,
  onAmountChange,
  className = '',
  closeModal,
  hideLearnMore = false,
  onPoolDataUpdate
}: I_TokenPurchaseProps) {
  const t = useTranslations('pools');
  const tPredictions = useTranslations('predictions');
  const poolContractAddress = selectedToken.poolAddress as `0x${string}`;
  const tokenAddress = selectedToken.tokenAddress as Address | undefined;
  const decimals = selectedToken.decimals;

  const { needsKYC, isKycLoading, openPersonaModal, remainingAmount } =
    usePersonaKYC();

  const {
    shouldImportToken,
    isImporting,
    showImportCheckbox,
    setShouldImportToken,
    importTokenToWallet
  } = useTokenImport({
    tokenAddress,
    tokenSymbol: selectedToken.tokenSymbol,
    fallbackDecimals: decimals
  });

  const [handleBuyTokensFromSwap, setHandleBuyTokensFromSwap] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isSwapBusy, setIsSwapBusy] = useState<boolean>(false);
  const [swapUsdcStr, setSwapUsdcStr] = useState<string>('');

  const tokensRemaining = calculateTokensRemaining(selectedToken);
  const tokensRemainingNum = parseFloat(tokensRemaining) || 0;
  const isTokensFinished = tokensRemainingNum <= 0;

  const handleBuyTokens = async () => {
    if (!handleBuyTokensFromSwap) return;
    const usdcAmount = parseFloat(removeCommas(swapUsdcStr)) || 0;
    // Check remaining KYC allowance before buying
    if (!needsKYC && remainingAmount > 0 && usdcAmount > remainingAmount) {
      toast.error(
        tPredictions('kycRemainingExceeded', {
          remaining: remainingAmount.toLocaleString()
        })
      );
      return;
    }
    await handleBuyTokensFromSwap();
  };

  const isBusy = isSwapBusy || isImporting;

  const loadingText = isSwapBusy
    ? t('loading')
    : isImporting
      ? t('importing')
      : '';

  const buyButtonDisabled =
    !swapUsdcStr ||
    parseFloat(removeCommas(swapUsdcStr)) <= 0 ||
    isTokensFinished ||
    !handleBuyTokensFromSwap;

  const buyButtonLabel = loadingText || t('buyTokens');

  return (
    <div className={cn('p-7 gap-4 grid pb-4', className)}>
      <OverlayLoading isVisible={isBusy} text={loadingText} />
      <SwapAmountInputs
        poolContractAddress={poolContractAddress}
        tokenDecimals={decimals}
        tokenSymbol={selectedToken.tokenSymbol}
        tokenAddress={tokenAddress}
        selectedToken={selectedToken}
        onPoolDataUpdate={onPoolDataUpdate}
        closeModal={closeModal}
        shouldImportToken={shouldImportToken}
        importTokenToWallet={importTokenToWallet}
        onBuyTokensReady={(handleBuy, isBusy, usdcStr) => {
          setHandleBuyTokensFromSwap(() => handleBuy);
          setIsSwapBusy(isBusy);
          setSwapUsdcStr(usdcStr);
        }}
      />

      {showImportCheckbox && (
        <TokenImportCheckbox
          checked={shouldImportToken}
          onCheckedChange={setShouldImportToken}
          disabled={isBusy}
          tokenAddress={tokenAddress}
          tokenSymbol={selectedToken.tokenSymbol}
        />
      )}

      {isTokensFinished ? (
        <div className="p-4">
          <P className="text-center text-destructive font-medium">
            {t('tokensSoldOut')}
          </P>
        </div>
      ) : (
        <Button
          variant={'brand'}
          size={'lg'}
          onClick={needsKYC ? openPersonaModal : handleBuyTokens}
          isLoading={needsKYC ? isKycLoading : isBusy}
          disabled={buyButtonDisabled}
        >
          {buyButtonLabel}
        </Button>
      )}
      {!hideLearnMore && (
        <Button variant="outline" size="lg" asChild>
          <Link
            href={C_Routes[E_Routes.POOLS_PRESALE].link}
            onClick={closeModal}
          >
            {t('learnMore')}
          </Link>
        </Button>
      )}
      <P className="text-center min-h-9 text-muted text-sm font-normal leading-tight">
        {t('unlockDescription')}
      </P>
    </div>
  );
}
